import { useState } from 'react';

import { useServiceRequest } from '@commenda/api';
import {
  createServiceRequestIssue,
  updateTask,
} from '@commenda/api/service-request';
import { SalesTaxFilingTasks } from '@commenda/types/src/sales-tax/types';
import { ServiceRequestEntityType, TaskStatus } from '@prisma/client';

import { ReportIssueModal } from '../../../service-request/common/modals/ReportIssueModal';

interface IndirectTaxFilingReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceRequestId: string;
  // Refetches the filing GET so the task bar re-derives (the ball moves back to
  // the agent) and the report-issue button hides.
  onReported: () => void;
}

// Client-facing "Report issue" on the filing's REVIEW_TRANSACTIONS step. Posts
// the issue to the service-request thread (via ReportIssueModal), records a
// ServiceRequestIssue, then sends the flow back to the agent: REVIEW_TRANSACTIONS
// → IN_PROGRESS + isIssueReported and GENERATE_FILING_REPORT → IN_PROGRESS.
const IndirectTaxFilingReportIssueModal = ({
  isOpen,
  onClose,
  serviceRequestId,
  onReported,
}: IndirectTaxFilingReportIssueModalProps) => {
  const { data: serviceRequest, mutate: refetchServiceRequest } =
    useServiceRequest(serviceRequestId);
  const [isReporting, setIsReporting] = useState(false);

  if (!serviceRequest) return null;

  const handleReport = async (message: string) => {
    if (isReporting) return;
    setIsReporting(true);
    try {
      const tasks =
        serviceRequest.taskGroups?.flatMap((group) => group.tasks) ?? [];
      const reviewTask = tasks.find(
        (task) =>
          task.legacyServiceRequestTaskTaskId ===
          SalesTaxFilingTasks.REVIEW_TRANSACTIONS
      );
      const generateReportTask = tasks.find(
        (task) =>
          task.legacyServiceRequestTaskTaskId ===
          SalesTaxFilingTasks.GENERATE_FILING_REPORT
      );

      if (reviewTask) {
        await createServiceRequestIssue({
          data: {
            message,
            entityType: ServiceRequestEntityType.SERVICE_REQUEST_STEP,
            entityId: String(reviewTask.id),
            serviceRequestId,
          },
        });
      }

      if (serviceRequest.workflowExecutionId) {
        // On a workflow SR the approve step's gateway reads
        // outputData.isIssueReported and loops back to the review step, so the
        // step just needs to complete with the flag set.
        if (reviewTask) {
          await updateTask({
            id: reviewTask.id,
            data: { status: TaskStatus.COMPLETED, isIssueReported: true },
          });
        }
      } else {
        await Promise.all([
          reviewTask
            ? updateTask({
                id: reviewTask.id,
                data: { status: TaskStatus.IN_PROGRESS, isIssueReported: true },
              })
            : Promise.resolve(),
          generateReportTask
            ? updateTask({
                id: generateReportTask.id,
                data: { status: TaskStatus.IN_PROGRESS },
              })
            : Promise.resolve(),
        ]);
      }

      await refetchServiceRequest?.();
      onReported();
      onClose();
    } finally {
      setIsReporting(false);
    }
  };

  return (
    <ReportIssueModal
      isOpen={isOpen}
      setIsOpen={(open) => {
        const next = typeof open === 'function' ? open(isOpen) : open;
        if (!next) onClose();
      }}
      onReport={handleReport}
      serviceRequest={serviceRequest}
      title="Report an issue with this filing"
      description="Describe what needs to change. We'll send this to the Commenda team and they'll revise the filing report."
    />
  );
};

export { IndirectTaxFilingReportIssueModal };
